import { AxiosHeaders, type InternalAxiosRequestConfig } from 'axios'
import posthog from 'posthog-js'
import { getPortalApiBaseUrl, isLocalPortalApiBaseUrlValue } from '@/platform/http/portalApiBaseUrl'
import {
  injectStytchAuthHeaders,
  injectLocalUserIdentity,
  type IdentityCacheValue,
  resolveStytchAuthForRequest,
} from '@/platform/http/portalApiAuth'
import { STYTCH_SESSION_JWT_COOKIE } from '@/utilities/stytch/constants'

const AUTH_USER_ID_OVERRIDE_HEADER = 'x-auth-user-id-override'

type AuthHeaders = {
  'x-auth-provider': string
  'x-auth-provider-record-id': string
  authUserId: string | null
  'x-auth-user-id-override'?: string
}

/**
 * Ensures the request config has a `headers` property that satisfies Zodios types for
 * auth endpoints (x-auth-provider, x-auth-provider-record-id). Merges with any
 * existing headers in config. The actual auth header values are injected at runtime
 * by the axios request interceptor; this function only satisfies the type checker.
 */
export const withAuthHeaders = <T extends Record<string, unknown>>(
  config: T,
): T & { headers: AuthHeaders } => {
  const existing =
    config.headers && typeof config.headers === 'object' && !Array.isArray(config.headers)
      ? (config.headers as Record<string, string>)
      : {}
  return { ...config, headers: { ...existing } } as T & { headers: AuthHeaders }
}

/** Alias for Next.js API callers that need `x-auth-user-id-override`. */
export const withNextApiHeaders = withAuthHeaders

let identityCache: IdentityCacheValue = null

const getSessionJwt = (): string | null => {
  if (globalThis.window === undefined) return null
  const match = document.cookie
    .split('; ')
    .map((c) => c.split('='))
    .find(([name]) => name === STYTCH_SESSION_JWT_COOKIE)
  return match?.[1] ?? null
}

const createRequestId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  // Prefer CSPRNG when randomUUID is unavailable.
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    globalThis.crypto.getRandomValues(bytes)
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const getPosthogSessionId = (): string | null => {
  if (globalThis.window === undefined) return null

  try {
    const sessionId = posthog.get_session_id()
    return sessionId || null
  } catch {
    return null
  }
}

/**
 * Clears the cached x-user-identity used by local Portal API requests.
 * Call during logout so the next user does not receive a stale identity.
 */
export const clearIdentityCache = (): void => {
  identityCache = null
}

function readAuthUserIdOverride(headers: AxiosHeaders): string | null {
  const rawAuthUserIdOverride = headers.get(AUTH_USER_ID_OVERRIDE_HEADER)
  headers.delete(AUTH_USER_ID_OVERRIDE_HEADER)
  return typeof rawAuthUserIdOverride === 'string' ? rawAuthUserIdOverride.trim() || null : null
}

function applyCachedLocalUserIdentity(
  headers: AxiosHeaders,
  jwt: string | null,
  authUserId: string | null,
): void {
  const portalApiBaseUrl = getPortalApiBaseUrl()
  if (!isLocalPortalApiBaseUrlValue(portalApiBaseUrl) || !jwt) return

  const authUserIdKey = authUserId ?? ''
  if (identityCache?.authUserId !== authUserIdKey) return
  headers.set('x-user-identity', identityCache.identity)
}

/**
 * Sets browser Portal API headers without waiting.
 * Page-hide saves must call fetch in the same turn, so this path uses the
 * cached local identity and does not fetch a new one.
 */
export function applyPortalApiBrowserRequestHeadersSync(config: InternalAxiosRequestConfig): {
  jwt: string | null
  authUserId: string | null
} | null {
  if (globalThis.window === undefined) return null

  const headers = AxiosHeaders.from(config.headers)
  headers.set('X-Request-ID', createRequestId())
  const authUserIdOverride = readAuthUserIdOverride(headers)

  const sessionId = getPosthogSessionId()
  if (sessionId) {
    headers.set('X-Session-ID', sessionId)
  }

  const jwt = getSessionJwt()
  const auth = resolveStytchAuthForRequest({ jwt, authUserIdOverride })
  injectStytchAuthHeaders(headers, auth)
  applyCachedLocalUserIdentity(headers, jwt, auth.authUserId)
  config.headers = headers

  return { jwt, authUserId: auth.authUserId }
}

/**
 * Injects tracing and auth headers shared by portalApiClient and nextApiClient.
 *
 * Local-dev `x-user-identity` is keyed off `getPortalApiBaseUrl()` (not the request
 * baseURL) so same-origin Next.js API calls still get the header when Portal API is local.
 */
export async function applyPortalApiRequestHeaders(
  config: InternalAxiosRequestConfig,
): Promise<void> {
  const browserAuth = applyPortalApiBrowserRequestHeadersSync(config)
  if (browserAuth) {
    const portalApiBaseUrl = getPortalApiBaseUrl()
    if (!isLocalPortalApiBaseUrlValue(portalApiBaseUrl)) return

    const headers = AxiosHeaders.from(config.headers)
    await injectLocalUserIdentity({
      baseUrl: portalApiBaseUrl,
      headers,
      jwt: browserAuth.jwt,
      authUserId: browserAuth.authUserId,
      cache: {
        get: () => identityCache,
        set: (value) => {
          identityCache = value
        },
      },
    })
    config.headers = headers
    return
  }

  const headers = AxiosHeaders.from(config.headers)
  headers.set('X-Request-ID', createRequestId())
  const authUserIdOverride = readAuthUserIdOverride(headers)

  let jwt: string | null = null
  try {
    const nextHeadersModule = await import('next/headers')
    const [cookieStore, requestHeaders] = await Promise.all([
      nextHeadersModule.cookies(),
      nextHeadersModule.headers(),
    ])

    jwt = cookieStore.get(STYTCH_SESSION_JWT_COOKIE)?.value ?? null
    const forwardedCookieHeader = requestHeaders.get('cookie')?.trim()
    if (forwardedCookieHeader) {
      headers.set('cookie', forwardedCookieHeader)
    }

    const sessionId = requestHeaders.get('x-session-id')?.trim()
    if (sessionId) {
      headers.set('X-Session-ID', sessionId)
    }
  } catch {
    // Ignore when called outside of a Next.js request context.
  }

  const auth = resolveStytchAuthForRequest({ jwt, authUserIdOverride })
  injectStytchAuthHeaders(headers, auth)

  const portalApiBaseUrl = getPortalApiBaseUrl()
  if (isLocalPortalApiBaseUrlValue(portalApiBaseUrl)) {
    await injectLocalUserIdentity({
      baseUrl: portalApiBaseUrl,
      headers,
      jwt,
      authUserId: auth.authUserId,
    })
  }

  config.headers = headers
}
